import { useCallback, useEffect, useRef, useState } from 'react';
import { Grid2, Box, Button, Typography } from '@mui/material';
import { Document, Page, pdfjs } from 'react-pdf';
import download from 'assets/images/download.jpg';
import { downloadFile } from 'services/assignment.service';
import Snackbar from 'utils/Snackbar';

pdfjs.GlobalWorkerOptions.workerSrc = `//unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`;

const protectedContentSx = {
  userSelect: 'none',
  WebkitUserSelect: 'none',
  MozUserSelect: 'none',
  msUserSelect: 'none',
  '& canvas': {
    userSelect: 'none',
    WebkitUserDrag: 'none'
  }
} as const;

const ProtectedPdfViewer = ({ fileUrl }: { fileUrl: string }) => {
  const [numPages, setNumPages] = useState(0);
  const [pageWidth, setPageWidth] = useState(800);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const updateWidth = () => {
      const available = container.clientWidth - 24;
      setPageWidth(Math.min(900, Math.max(280, available)));
    };

    updateWidth();
    const observer = new ResizeObserver(updateWidth);
    observer.observe(container);
    return () => observer.disconnect();
  }, []);

  const blockClipboard = useCallback((event: React.ClipboardEvent) => {
    event.preventDefault();
  }, []);

  const blockContextMenu = useCallback((event: React.MouseEvent) => {
    event.preventDefault();
  }, []);

  return (
    <Box
      ref={containerRef}
      onContextMenu={blockContextMenu}
      onCopy={blockClipboard}
      onCut={blockClipboard}
      onDragStart={(event) => event.preventDefault()}
      sx={{
        ...protectedContentSx,
        height: '70vh',
        width: '100%',
        overflow: 'auto',
        borderRadius: 2,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        '& .react-pdf__Document': {
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          width: '100%'
        },
        '& .react-pdf__Page': {
          display: 'flex',
          justifyContent: 'center',
          marginBottom: 1
        },
        '& .react-pdf__Page__canvas': {
          display: 'block',
          margin: '0 auto'
        }
      }}
    >
      <Document
        file={fileUrl}
        onLoadSuccess={({ numPages: pages }) => setNumPages(pages)}
        onLoadError={() => Snackbar('Unable to load PDF preview.', 'error')}
        loading={<Typography sx={{ py: 4 }}>Loading PDF...</Typography>}
      >
        {Array.from({ length: numPages }, (_, index) => (
          <Box key={`protected_page_${index + 1}`} sx={{ display: 'flex', justifyContent: 'center', width: '100%' }}>
            <Page
              pageNumber={index + 1}
              width={pageWidth}
              renderTextLayer={false}
              renderAnnotationLayer={false}
            />
          </Box>
        ))}
      </Document>
    </Box>
  );
};

const DocumentViewer = ({
  currentContentData,
  disableFlag,
  restrictContentAccess
}: {
  currentContentData: any;
  disableFlag?: any;
  /** When true (student course view), PDFs are view-only: no download toolbar path, no copy/paste. */
  restrictContentAccess?: boolean;
}) => {
  // Helper functions
  const getFileType = (file: any) => {
    if (!file) return '';
    return file.mimetype?.split('/').pop() || '';
  };

  const normalizedFile =
    Array.isArray(currentContentData?.file) && currentContentData.file.length > 0
      ? currentContentData.file[0]
      : currentContentData?.file;

  const isPdfContent = () => {
    const mimetype = normalizedFile?.mimetype || currentContentData?.mimetype;
    const extensionFromBlob = currentContentData?.azureBlobUrl?.split('.').pop()?.split('?')[0]?.toLowerCase();
    return (
      currentContentData?.type === 'pdf' ||
      mimetype === 'application/pdf' ||
      getFileType(currentContentData?.file) === 'pdf' ||
      extensionFromBlob === 'pdf'
    );
  };
  const handleMouseUp = () => {
    // Add your mouse up handler logic here
  };

  const downloadFiles = async (cont_id: any, name: any) => {
    try {
      const response = await downloadFile({ fileId: cont_id });

      const contentType = response.headers['content-type'];

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', name);
      if (contentType) {
        const ext = contentType.split('/').pop();
        link.setAttribute('download', `${name}.${ext}`);
      }
      document.body.appendChild(link);
      link.click();
      Snackbar('Course Content Downloaded Successfully.', 'success');
    } catch (error) {
      Snackbar('Unable to download the file', 'error');
    }
  };

  // Get file URL helper
  const getFileUrl = () => {
    if (currentContentData?.azureBlobUrl) {
      return `${process.env.REACT_APP_AZURE_BLOB_BASE_URL}/${currentContentData.azureBlobUrl}`;
    }
    if (normalizedFile?.fileUrl) return normalizedFile.fileUrl;
    if (normalizedFile?.path && currentContentData?.storageType !== 'local') return normalizedFile.path;
    if (currentContentData?.path && currentContentData?.storageType !== 'local') return currentContentData.path;
    if (normalizedFile?.mimetype && normalizedFile?.file) {
      return `data:${normalizedFile.mimetype};base64,${normalizedFile.file}`;
    }
    return '';
  };
  
  // Check if file is Office document
  const isOfficeDocument = () => {
    const officeTypes = [
      'application/msword',
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
      'application/vnd.openxmlformats-officedocument.presentationml.presentation',
      'application/vnd.ms-powerpoint'
    ];
    const officeExtensions = ['doc', 'docx', 'ppt', 'pptx'];
  
    const currentUrl = getFileUrl();
    const extensionFromUrl = currentUrl?.split('.').pop()?.split('?')[0]?.toLowerCase();
    const mimetype = normalizedFile?.mimetype || currentContentData?.mimetype;
  
    return (
      (mimetype && officeTypes.includes(mimetype)) ||
      (currentContentData?.azureBlobUrl &&
        officeExtensions.includes(currentContentData.azureBlobUrl.split('.').pop()?.toLowerCase())) ||
      (extensionFromUrl && officeExtensions.includes(extensionFromUrl))
    );
  };

  // Render functions
  const renderOfficeViewer = () => (
    <iframe
    src={`https://view.officeapps.live.com/op/embed.aspx?src=${encodeURIComponent(getFileUrl())}`}
    width="100%"
    height="450px"
    title="Embedded Office Presentation"
  />
  );

  const renderWordDownload = () => (
    <div style={{ textAlign: 'center', width: '100%', paddingTop: "20px" }}>
      <Box
        sx={{
          p: 1,
          py: 1.5,
          maxWidth: '750px',
          maxHeight: 'auto',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexDirection: 'column',
          margin: 'auto'
        }}
      >
        <img
          src={download}
          style={{ padding: '20px', width: '80%', height: '80%' }}
          alt="download"
          loading="lazy"
        />
        <Button
          color="primary"
          variant="outlined"
          onClick={() => downloadFiles(currentContentData?._id, currentContentData?.name)}
          sx={{ my: 3, ml: 2 }}
          disabled={!disableFlag}
        >
          <Typography color="primary">
            Download Copy of <span style={{ color: '#66D6F7' }}>{currentContentData?.name}</span>
          </Typography>
        </Button>
      </Box>
    </div>
  );

  const renderDefaultViewer = () => {
    const fileUrl = getFileUrl();
    if (restrictContentAccess && isPdfContent() && fileUrl) {
      return <ProtectedPdfViewer fileUrl={fileUrl} />;
    }

    return (
      <Box
        className="textDiv"
        onMouseUp={handleMouseUp}
        sx={{
          color: 'black',
          height: '70vh',
          width: '100%',
          textAlign: getFileType(currentContentData?.file) === 'pdf' ? 'center' : 'initial',
          borderRadius: 2
        }}
      >
        <iframe
          src={fileUrl}
          width="100%"
          height="100%"
          style={{ border: 'none' }}
          allowFullScreen
          allow="fullscreen"
          title="File Viewer"
        />
      </Box>
    );
  };



  // Main render
  if (!currentContentData?.file && !currentContentData?.azureBlobUrl) {
    return null;
  }

  return (
    <Grid2 sx={{ width: 'inherit', px: 3, marginTop: 2, borderRadius: 2 }}>
      {isOfficeDocument() ? renderOfficeViewer() :
        currentContentData?.file?.mimetype === 'application/msword' ? renderWordDownload() :
          renderDefaultViewer()}
    </Grid2>
  );
};

export default DocumentViewer;